import axios from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import UserContext from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import authenticate from '../../utils/Authenticate';
import displayError from '../../utils/ErrorHandler';

const ChangePassword = () => {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function startup() {
      if (!user) {
        const u = await authenticate(setUser, navigate);
        if (!u) return;
        if (u.role !== 'SuperAdmin') { navigate('/'); return; }
      } else if (user.role !== 'SuperAdmin') {
        navigate('/');
      }
    }
    startup();
  }, [user, navigate, setUser]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const submitData = (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      setNotification({ message: 'New password and confirm password do not match!', type: 'error' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (formData.newPassword.length < 6) {
      setNotification({ message: 'New password must be at least 6 characters!', type: 'error' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);
    axios.post(
      `${process.env.REACT_APP_API_URI}/api/users/change-password`,
      { currentPassword: formData.currentPassword, newPassword: formData.newPassword },
      { withCredentials: true }
    )
      .then((response) => {
        setNotification({ message: response.data.message, type: 'success' });
        setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch((err) => {
        const errorMessage = displayError(err, navigate, setUser);
        if (errorMessage) {
          setNotification({ message: errorMessage, type: 'error' });
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-4 lg:px-8">
      <h2 className="text-xl font-semibold text-center text-fmb mb-6">Change Password</h2>

      {notification.message && (
        <div
          className={`m-2 mb-4 p-4 rounded ${notification.type === 'success' ? 'bg-green-200 border-green-600 text-green-600' : 'bg-red-200 border-red-600 text-red-600'}`}
        >
          {notification.message}
        </div>
      )}

      <form className="space-y-6 max-w-md w-full mx-auto" onSubmit={submitData}>
        <div>
          <label htmlFor="currentPassword" className="block text-sm font-medium leading-6 text-gray-900">
            Current Password
          </label>
          <div className="mt-2">
            <input
              id="currentPassword"
              name="currentPassword"
              type="password"
              placeholder="Current Password"
              autoComplete="current-password"
              value={formData.currentPassword}
              onChange={handleChange}
              required
              className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium leading-6 text-gray-900">
            New Password
          </label>
          <div className="mt-2">
            <input
              id="newPassword"
              name="newPassword"
              type="password"
              placeholder="New Password"
              autoComplete="new-password"
              value={formData.newPassword}
              onChange={handleChange}
              required
              minLength={6}
              className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium leading-6 text-gray-900">
            Confirm New Password
          </label>
          <div className="mt-2">
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder="Confirm New Password"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              minLength={6}
              className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={submitting}
            className="flex w-full justify-center rounded-full bg-fmb px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-yellow-600 disabled:opacity-60 transition-colors"
          >
            {submitting ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;
